/* Profile Modal — lets the signed-in user upload their company logo,
 * profile picture, voided check and W9, and change their own password.
 * Opens when the user clicks "Profile" in the header menu.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Upload, Trash2, Eye, KeyRound, Loader2, FileText, Image as ImageIcon, FileCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FILE_KINDS = [
  { kind: 'profile_picture',  label: 'Profile Picture', accept: 'image/*', icon: ImageIcon, hint: 'JPG / PNG, square works best' },
  { kind: 'company_logo',     label: 'Company Logo',    accept: 'image/*', icon: ImageIcon, hint: 'JPG / PNG / SVG, transparent background preferred' },
  { kind: 'voided_check',     label: 'Voided Check',    accept: 'application/pdf,image/*', icon: FileCheck, hint: 'PDF or clear photo of the check' },
  { kind: 'w9_form',          label: 'W9 Form',         accept: 'application/pdf,image/*', icon: FileText, hint: 'PDF preferred' },
];

const FileSlot = ({ slot, present, token, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInput = useRef(null);
  const Icon = slot.icon;

  // Build a fresh viewer URL whenever `present` flips on.
  useEffect(() => {
    if (!present) {
      setPreviewUrl(null);
      return;
    }
    // We can't put the Authorization header on a plain <img> tag → fetch as
    // blob and use object URL for the preview.
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/profile/file/${slot.kind}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        if (cancelled) return;
        const url = URL.createObjectURL(resp.data);
        setPreviewUrl(url);
      } catch (_e) {
        if (!cancelled) setPreviewUrl(null);
      }
    })();
    return () => { cancelled = true; if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [present, slot.kind]);

  const handlePick = () => fileInput.current?.click();

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10 MB limit');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await axios.post(`${BACKEND_URL}/api/profile/upload/${slot.kind}`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${slot.label} updated`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove your ${slot.label.toLowerCase()}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/profile/file/${slot.kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${slot.label} removed`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  const isImage = slot.kind === 'profile_picture' || slot.kind === 'company_logo';

  return (
    <Card className="border-slate-200" data-testid={`profile-slot-${slot.kind}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-600" />
          <div className="flex-1">
            <p className="font-medium text-sm">{slot.label}</p>
            <p className="text-[11px] text-slate-500">{slot.hint}</p>
          </div>
          {present && <span className="text-[10px] uppercase font-semibold tracking-wide text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">Uploaded</span>}
        </div>

        {present && (
          <div className="border border-slate-200 rounded-md bg-slate-50 p-2 flex items-center justify-center min-h-[80px]">
            {isImage && previewUrl ? (
              <img src={previewUrl} alt={slot.label} className="max-h-24 object-contain" />
            ) : present && !isImage ? (
              <div className="text-xs text-slate-600 text-center">
                <FileText className="h-6 w-6 mx-auto mb-1 text-slate-500" />
                File on record
              </div>
            ) : (
              <span className="text-xs text-slate-400">Loading preview…</span>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInput}
            type="file"
            accept={slot.accept}
            className="hidden"
            onChange={handleUpload}
            data-testid={`profile-input-${slot.kind}`}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={handlePick}
            disabled={busy}
            data-testid={`profile-upload-${slot.kind}`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
            {present ? 'Replace' : 'Upload'}
          </Button>
          {present && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const r = await axios.get(`${BACKEND_URL}/api/profile/file/${slot.kind}`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob',
                    });
                    const url = URL.createObjectURL(r.data);
                    window.open(url, '_blank');
                  } catch (_e) {
                    toast.error('Could not open file');
                  }
                }}
                disabled={busy}
                data-testid={`profile-view-${slot.kind}`}
              >
                <Eye className="h-3.5 w-3.5 mr-1" /> View
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRemove}
                disabled={busy}
                className="border-red-200 text-red-700 hover:bg-red-50"
                data-testid={`profile-remove-${slot.kind}`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const ChangePasswordCard = ({ token }) => {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/profile/change-password`, {
        current_password: cur,
        new_password: next,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password updated');
      setCur(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Password change failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="profile-change-password">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-slate-600" />
          <p className="font-medium text-sm">Change Password</p>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-600">Current Password</Label>
            <Input
              type="password"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              autoComplete="current-password"
              required
              className="bg-white text-slate-900"
              data-testid="profile-current-password"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">New Password</Label>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
              className="bg-white text-slate-900"
              data-testid="profile-new-password"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Confirm New Password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
              className="bg-white text-slate-900"
              data-testid="profile-confirm-password"
            />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button
              type="submit"
              disabled={busy}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="profile-change-password-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <KeyRound className="h-3.5 w-3.5 mr-1" />}
              Change Password
            </Button>
          </div>
        </form>
        <p className="text-[11px] text-slate-500">
          Minimum 8 characters. Use a unique password not reused elsewhere.
        </p>
      </CardContent>
    </Card>
  );
};

const ProfileModal = ({ open, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/profile/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="profile-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>My Profile</span>
            {profile?.email && <span className="text-xs font-normal text-slate-500">— {profile.email}</span>}
          </DialogTitle>
        </DialogHeader>

        {loading || !profile ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {FILE_KINDS.map((slot) => (
                <FileSlot
                  key={slot.kind}
                  slot={slot}
                  present={!!profile.files?.[slot.kind]}
                  token={token}
                  onChange={load}
                />
              ))}
            </div>

            <ChangePasswordCard token={token} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;
